import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Button, Dropdown, Spinner } from "react-bootstrap";
import InfoTooltip from "app/components/common/InfoTooltip";
import Icon from "@mdi/react";
import { mdiCogOutline, mdiClose, mdiLinkOff } from "@mdi/js";
import googleCalendarIcon from "./images/google-calendar.svg";
import microsoft365Icon from "./images/microsoft-365.svg";
import DisconnectCalendarModal from "./DisconnectCalendarModal";
import {
  fetchCalendarProviders,
  disconnectCalendarProvider,
  initiateOAuth,
} from "./slice";
import "./calendarConnection.css";

const PROVIDER_ICONS = {
  google: { src: googleCalendarIcon, alt: "Google Calendar" },
  microsoft: { src: microsoft365Icon, alt: "Microsoft 365" },
};

const CONNECT_OPTIONS = [
  { key: "google", label: "Google Calendar", icon: googleCalendarIcon },
  { key: "microsoft", label: "Microsoft 365", icon: microsoft365Icon },
];

const PROVIDER_TYPE_MAP = {
  office365: "microsoft",
  google: "google",
};

const COMPONENT_TO_API_MAP = {
  microsoft: "office365",
  google: "google",
};

const HIDDEN_STATUSES = ["revoked", "orphaned"];

const mapProviderToConnection = (provider) => ({
  id: provider.id,
  provider: PROVIDER_TYPE_MAP[provider.provider_type] || provider.provider_type,
  email: provider.email_identifier,
  connected: provider.status === "active",
});

const ConnectAccountDropdown = ({ onConnect, size, disabled }) => (
  <Dropdown align="end">
    <Dropdown.Toggle variant="outline-primary" size={size} disabled={disabled}>
      <Icon path={mdiCogOutline} size="16px" className="me-1" />
      Connect Account
    </Dropdown.Toggle>
    <Dropdown.Menu>
      {CONNECT_OPTIONS.map(({ key, label, icon }) => (
        <Dropdown.Item
          key={key}
          onClick={() => onConnect(key)}
          className="d-flex align-items-center gap-2"
        >
          <img src={icon} alt={label} width={24} height={24} />
          {label}
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);

const CalendarConnection = () => {
  const dispatch = useDispatch();
  const {
    calendarProviders,
    isCalendarProvidersLoading,
    isDisconnectingProvider,
    calendarProvidersError,
    isInitiatingOAuth,
    oauthError,
  } = useSelector((state) => state.userProfile);

  const connections = calendarProviders
    .filter(
      (p) =>
        p.provider_type !== "virtual" && !HIDDEN_STATUSES.includes(p.status)
    )
    .map(mapProviderToConnection);

  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState(null);

  useEffect(() => {
    dispatch(fetchCalendarProviders());
  }, [dispatch]);

  const handleConnectAccount = async (providerKey) => {
    const apiProvider = COMPONENT_TO_API_MAP[providerKey];
    if (!apiProvider) return;

    try {
      const response = await dispatch(initiateOAuth(apiProvider)).unwrap();
      if (response?.auth_url) {
        window.location.href = response.auth_url;
      }
    } catch (error) {
      // Error is stored in Redux state and displayed via oauthError alert
    }
  };

  const handleDisconnect = (connection) => {
    setDisconnectTarget(connection);
    setShowDisconnectModal(true);
  };

  const handleConfirmDisconnect = async () => {
    if (!disconnectTarget) return;
    try {
      await dispatch(disconnectCalendarProvider(disconnectTarget.id)).unwrap();
      setShowDisconnectModal(false);
      setDisconnectTarget(null);
    } catch (error) {
      // Keep modal open so user can see error or retry
    }
  };

  const handleReconnect = (connection) => {
    handleConnectAccount(connection.provider);
  };

  if (isCalendarProvidersLoading) {
    return (
      <div className="calendar-connection-loading">
        <h2 className="mb-0">Calendar & Email Connection</h2>
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="calendar-connection-section">
        <div className="calendar-connection-header">
          <div className="calendar-connection-title-row">
            <h2 className="mb-0">Calendar & Email Connection</h2>
            {connections.length > 0 && (
              <ConnectAccountDropdown
                onConnect={handleConnectAccount}
                size="sm"
                disabled={isInitiatingOAuth}
              />
            )}
          </div>
          <p className="calendar-connection-subtitle">
            Connect an account (e.g., Google) to sync Morph jobs to your
            external calendar and enable automated emails.{" "}
            <InfoTooltip
              text="Changes made in external calendars will not sync back to Morph."
              container={document.body}
              className="calendar-info-tooltip"
            />
          </p>
        </div>

        {calendarProvidersError && (
          <Alert variant="danger" className="mt-2">
            {typeof calendarProvidersError === "string"
              ? calendarProvidersError
              : "Failed to load calendar connections."}
          </Alert>
        )}

        {oauthError && (
          <Alert variant="danger" className="mt-2">
            {typeof oauthError === "string"
              ? oauthError
              : "Failed to initiate calendar connection. Please try again."}
          </Alert>
        )}

        <div className="calendar-connection-list">
          {connections.length === 0 && (
            <div className="calendar-connection-empty">
              <p className="calendar-connection-empty-text">No connection yet</p>
              <ConnectAccountDropdown
                onConnect={handleConnectAccount}
                disabled={isInitiatingOAuth}
              />
            </div>
          )}
          {connections.map((connection, index) => {
            const providerIcon = PROVIDER_ICONS[connection.provider];
            return (
              <div key={connection.id}>
                {index > 0 && <hr className="calendar-connection-divider" />}
                <div className="calendar-connection-item">
                  <button
                    type="button"
                    className="calendar-remove-btn"
                    onClick={() => handleDisconnect(connection)}
                    aria-label={`Remove ${connection.email}`}
                  >
                    <Icon path={mdiClose} size="24px" />
                  </button>
                  {providerIcon && (
                    <img
                      src={providerIcon.src}
                      alt={providerIcon.alt}
                      className="calendar-provider-icon"
                    />
                  )}
                  <span className="calendar-connection-email">
                    {connection.email}
                  </span>
                  {!connection.connected && (
                    <>
                      <Icon
                        path={mdiLinkOff}
                        size="20px"
                        className="calendar-disconnected-icon"
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        className="ms-auto"
                        onClick={() => handleReconnect(connection)}
                      >
                        Re-connect
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <DisconnectCalendarModal
        show={showDisconnectModal}
        onHide={() => setShowDisconnectModal(false)}
        onConfirm={handleConfirmDisconnect}
        email={disconnectTarget?.email}
        isDisconnecting={isDisconnectingProvider}
      />
    </>
  );
};

export default CalendarConnection;
